import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import {
  Select,
  SelectOption,
  RangePicker as DateRangePicker,
} from "@shape-ds/components";
import { dayjs, Dayjs } from "../../lib";
import "./style.scss";
import { EDateFormats } from "../../utils";

type DateRange = [Dayjs | null, Dayjs | null] | null;

export interface IDateFiltersProps {
  modelName?: string;
  defaultPeriod?: number | string;
  periodOptions: SelectOption[];
  enableCalendar: boolean;
  handlePeriodChange?: (value: string) => void;
  handleCalendarChange?: (range: {
    startDate: string;
    endDate: string;
  }) => void;
  handleClear?: () => void;
  enableCustomPeriod?: boolean;
  dateFormat?: string;
  maxMonthsRange?: number;
  className?: string;
  defaultDateRange?: DateRange;
}

export const DateFilters = ({
  defaultPeriod,
  periodOptions,
  enableCalendar,
  handleCalendarChange,
  handlePeriodChange,
  handleClear,
  enableCustomPeriod,
  dateFormat = EDateFormats.API_DATE_FORMAT,
  maxMonthsRange,
  className,
  defaultDateRange,
  modelName,
}: IDateFiltersProps) => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<DateRange>(
    defaultDateRange ?? null
  );
  const [value, setValue] = useState<DateRange>(defaultDateRange ?? null);
  const [openCalendar, setOpenCalendar] = useState(!!defaultDateRange);
  const [error, setError] = useState<string>();

  const defaultPeriodOption = periodOptions.find(
    (option) => option.value === defaultPeriod
  );

  const periodRange = defaultDateRange
    ? "custom"
    : (defaultPeriodOption || periodOptions[0]).value;
  const [period, setPeriod] = useState(periodRange);

  const customOption =
    openCalendar || defaultDateRange || enableCustomPeriod
      ? [{ label: t("components:date_filter.custom"), value: "custom" }]
      : [];

  const onOpenChange = (open: boolean) => {
    if (open) {
      setDateRange([null, null]);
    } else {
      setDateRange(null);
    }
  };

  useEffect(() => {
    if (defaultDateRange) {
      setOpenCalendar(true);
      setDateRange(defaultDateRange);
      setValue(defaultDateRange);
      setPeriod("custom");
    }
  }, [defaultDateRange]);

  useEffect(() => {
    if (defaultPeriod) {
      setPeriod(defaultPeriod as string);
    }
  }, [modelName]);

  return (
    <div
      className={`date-filter-wrapper ${className}`}
      data-testid="date-filter"
    >
      <Select
        data={[...periodOptions, ...customOption]}
        onChange={(event) => {
          setDateRange(null);
          const selectedValue = event.target.value as string;

          setPeriod(selectedValue);
          if (selectedValue === "custom") {
            setError("");
            setOpenCalendar(true);
            setDateRange([null, null]);
            return;
          }

          if (handlePeriodChange) {
            handlePeriodChange(selectedValue);
          }
          setError("");
          setOpenCalendar(false);
        }}
        className="date-filter-select"
        fullWidth
        value={period}
        data-testid="date-filter-select"
      />

      {enableCustomPeriod && (
        <div className={`date-range-wrapper ${openCalendar ? "" : "icon"}`}>
          {!openCalendar ? (
            <div
              className="date-range-picker-icon"
              onClick={() => {
                setOpenCalendar(!openCalendar);
                setDateRange([null, null]);
                setPeriod("custom");
              }}
            >
              <CalendarMonthOutlinedIcon color="inherit" />
            </div>
          ) : (
            <DateRangePicker
              value={dateRange || value}
              format={t("formats:date.lts")}
              onChange={(range) => {
                setValue(range);
                // Cast to DateRange to avoid type error when use internal dayjs

                const overridedRange = range as DateRange;

                if (overridedRange && overridedRange.length > 1) {
                  const isValidRange = maxMonthsRange
                    ? Math.abs(
                        dayjs(overridedRange[0]).diff(
                          dayjs(overridedRange[1]),
                          "months"
                        )
                      ) < maxMonthsRange
                    : true;

                  if (!isValidRange) {
                    setError(
                      t("components:date_filter.invalid_range", {
                        maxMonthsRange,
                      })
                    );

                    return;
                  }
                  setError("");
                  setDateRange([
                    dayjs(overridedRange[0]),
                    dayjs(overridedRange[1]),
                  ]);
                  if (handleCalendarChange) {
                    handleCalendarChange({
                      startDate: dayjs(overridedRange[0]).format(dateFormat),
                      endDate: dayjs(overridedRange[1])
                        .set("hour", 23)
                        .minute(59)
                        .format(dateFormat),
                    });
                  }
                }
              }}
              placeholder={[
                t("components:date_filter.start_date"),
                t("components:date_filter.end_date"),
              ]}
              disabled={!enableCalendar}
              className={`MuiSelect-select date-range-picker ${
                error ? "error" : ""
              }`}
              onOpenChange={onOpenChange}
              onCalendarChange={(val) => {
                setError("");
                setDateRange(val);
              }}
              allowClear
              clearIcon={
                <CloseOutlinedIcon
                  onClick={() => {
                    setOpenCalendar(false);
                    setPeriod(
                      defaultPeriod
                        ? defaultPeriod?.toString()
                        : periodOptions[0].value
                    );
                    setDateRange(null);
                    setError("");
                    handleClear?.();
                  }}
                />
              }
              disabledDate={(current: Dayjs) => {
                if (!current || !dateRange || !maxMonthsRange) {
                  return false;
                }

                const tooLate =
                  dateRange[0] &&
                  current.diff(dateRange[0], "months") >= maxMonthsRange;
                const tooEarly =
                  dateRange[1] &&
                  dateRange[1].diff(current, "months") >= maxMonthsRange;
                return !!tooEarly || !!tooLate || current?.isAfter(dayjs());
              }}
              suffixIcon={<CalendarMonthOutlinedIcon />}
            />
          )}
          {error && <p className="error">{error}</p>}
        </div>
      )}
    </div>
  );
};
